import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Building, Activity, Shield, AlertTriangle, Clock, Mail, UserCheck, Edit, Trash2, UserPlus, X } from 'lucide-react';
import AuthContext from '../../../../context/AuthContext';
import { useToast } from '../../../../context/ToastContext';
import useLoader from '../../../../context/useLoader';
import processService from '../../../../services/processService';
import taskService from '../../../../services/taskService';

export default function CartographDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasRole, authTokens } = useContext(AuthContext);
  const { showSuccess, showError } = useToast();
  const { isLoading, showLoader, hideLoader } = useLoader();
  
  const [process, setProcess] = useState(null);
  const [isCreatingTask, setIsCreatingTask] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ show: false, type: null, actionId: null, activityId: null });

  useEffect(() => {
    fetchProcessDetails();
  }, [id]);

  const fetchProcessDetails = async () => {
    showLoader('Chargement des détails...');
    
    try {
      const result = await processService.getProcessDetails(id, authTokens.access);
      
      if (result.success) {
        setProcess(result.data);
      } else {
        showError(`Erreur lors du chargement: ${result.error}`);
        setTimeout(() => navigate(-1), 2000);
      }
    } catch (error) {
      console.error('Error fetching process details:', error);
      showError('Impossible de charger les détails du processus');
      setTimeout(() => navigate(-1), 2000);
    } finally {
      hideLoader();
    }
  };

  const handleEdit = () => {
    navigate(`/dashboard/cartographies/edit/${id}`);
  };

  const handleCreateTask = async (riskActionId, activityId) => {
    setIsCreatingTask(riskActionId);

    console.log("creating tasks")

    try {
      const taskData = {
        process_id: process.id,
        process_activity_id: activityId,
        activity_risk_action_id: riskActionId,
      };

      console.log(taskData)

      const result = await taskService.createTask(taskData, authTokens.access);

      if (result.success) {
        showSuccess('Tâche assignée avec succès!');
        fetchProcessDetails();
      } else {
        if (result.status === 409) {
          showError('Cette tâche a déjà été assignée.');
        } else {
          showError(`Erreur: ${result.error || 'Impossible d\'assigner la tâche.'}`);
        }
      }
    } catch (error) {
      console.error('Error creating task:', error);
      showError('Une erreur est survenue lors de la création de la tâche.');
    } finally {
      setIsCreatingTask(null);
      setConfirmDialog({ show: false, type: null, actionId: null });
    }
  };

  const handleDelete = async () => {
    showLoader('Suppression en cours...');
    setConfirmDialog({ show: false, type: null, actionId: null });
    
    try {
      const result = await processService.deleteProcess(id, authTokens.access);
      
      if (result.success) {
        showSuccess('Processus supprimé avec succès!');
        setTimeout(() => navigate('/dashboard/cartographies'), 1500);
      } else {
        showError(`Erreur lors de la suppression: ${result.error}`);
      }
    } catch (error) {
      console.error('Error deleting process:', error);
      showError('Une erreur est survenue lors de la suppression');
    } finally {
      hideLoader();
    }
  };



  
  const canViewAllProcesses = () => {
    return hasRole("Admin") || hasRole("RiskManager");
  };

  const canEditProcess = () => {
    return hasRole("RiskManager");
  };

  const canDeleteProcess = () => {
    return hasRole("Admin");
  };

  const canAssignTasks = (hasTask) => {
    return hasRole("RiskManager") && !hasTask;
  };

  const ConfirmPopup = () => {
    if (!confirmDialog.show) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5 animate-fadeIn">
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900">
              {confirmDialog.type === 'delete' ? 'Confirmer la suppression' : 'Confirmer l\'assignation'}
            </h3>
            <button
              onClick={() => setConfirmDialog({ show: false, type: null, actionId: null, activityId: null })}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <p className="text-sm text-gray-600 mb-6">
            {confirmDialog.type === 'delete' 
              ? 'Êtes-vous sûr de vouloir supprimer ce processus? Cette action est irréversible.'
              : 'Êtes-vous sûr de vouloir assigner cette tâche ?'}
          </p>
          
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setConfirmDialog({ show: false, type: null, actionId: null, activityId:null })}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={() => {
                if (confirmDialog.type === 'delete') {
                  handleDelete();
                } else {
                  handleCreateTask(confirmDialog.actionId, confirmDialog.activityId);
                }
              }}
              className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                confirmDialog.type === 'delete' 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              Confirmer
            </button>
          </div>
        </div>
      </div>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-300 text-gray-800', label: 'Brouillon' },
      published: { color: 'bg-green-100 text-green-800', label: 'Publié' },
      archived: { color: 'bg-red-100 text-red-800', label: 'Archivé' }
    };
    
    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getProbabilityBadge = (probability) => {
    const probConfig = {
      low: { color: 'bg-green-100 text-green-800', label: 'Mineur' },
      mineur: { color: 'bg-green-100 text-green-800', label: 'Mineur' },

      normal: { color: 'bg-yellow-100 text-yellow-800', label: 'Normal' },
      moyenne: { color: 'bg-yellow-100 text-yellow-800', label: 'Normal' },

      high: { color: 'bg-red-100 text-red-800', label: 'Majeur' },
      majeur: { color: 'bg-red-100 text-red-800', label: 'Majeur' },

      critique: { color: 'bg-red-200 text-red-800', label: 'Critique' },
      critical: { color: 'bg-red-200 text-red-800', label: 'Critique' }
    };
    
    const config = probConfig[probability?.toLowerCase()] || { color: 'bg-gray-100 text-gray-800', label: probability || 'Non définie' };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non définie';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  if (!process) {
    return <div className="flex justify-center items-center h-64 text-sm">Chargement...</div>;
  }

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
      
      <ConfirmPopup />
      
      <div className="max-w-6xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button 
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={isLoading}
            >
              <ArrowLeft className="h-5 w-5 text-gray-500" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{process.process_name}</h1>
              <p className="text-xs text-gray-500">Détails de la cartographie</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {getStatusBadge(process.status)}

            {/* {canDeleteProcess() &&(
            <button
              onClick={handleEdit}
              className="flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              disabled={isLoading}
            >
              <Edit className="h-4 w-4 mr-1.5" />
              Modifier
            </button>

            )} */}

            {canDeleteProcess() && (
              <button
                onClick={() => setConfirmDialog({ show: true, type: 'delete', actionId: null })}
                className="flex items-center px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                disabled={isLoading}
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                Supprimer
              </button>
            )}
          </div>
        </div>

        {/* Process Information */}
        <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
          <div className="flex items-center mb-4">
            <Building className="h-4 w-4 text-teal-600 mr-2" />
            <h2 className="text-base font-semibold text-gray-900">Informations du Processus</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date du Processus</label>
              <div className="flex items-center text-sm text-gray-900">
                <Calendar className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                {formatDate(process.process_date)}
              </div>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Éléments à Contrôler</label>

              {process.control_elements_list && process.control_elements_list.length > 0 ? (
                <ul className="space-y-1">
                  {process.control_elements_list.map((elem) => (
                    <li key={elem.id} className="text-sm text-gray-900 flex items-center gap-2">
                      <span className="font-medium">{elem.element_name || "Sans nom"}</span>
                      <span className="text-xs text-gray-500">
                        <Calendar className="h-3 w-3 inline-block mr-1" />
                        {formatDate(elem.control_date)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500">Aucun élément défini</p>
              )}
            </div>

            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Point Focal</label>
              <div className="flex items-center text-sm text-gray-900">
                <User className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                {process.focal_point || 'Non défini'}
              </div>
            </div>
            
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Créé par</label>
              <p className="text-sm text-gray-900">
                {process.creator_details ? 
                  `${process.creator_details.first_name} ${process.creator_details.last_name}` : 
                  'Non défini'
                }
              </p>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Dernière mise à jour</label>
              <p className="text-sm text-gray-900">
                {process.last_updater_details ? 
                  `${process.last_updater_details.first_name} ${process.last_updater_details.last_name}` : 
                  'Non défini'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Activities and Risks */}
        {process.associated_activities && process.associated_activities.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
            <div className="flex items-center mb-4">
              <Activity className="h-4 w-4 text-blue-600 mr-2" />
              <h2 className="text-base font-semibold text-gray-900">Activités et Risques Associés</h2>
            </div>
            
            <div className="space-y-4">
              {process.associated_activities.map((activity, index) => (
                <div key={activity.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-base font-medium text-gray-900">{activity.activity_name} {activity.activity_id}</h3>
                      {activity.activity_description && (
                        <p className="text-xs text-gray-600 mt-1">{activity.activity_description}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Activity Risks */}
                  {activity.activity_risks && activity.activity_risks.length > 0 && (
                    <div className="mt-3">
                      <h4 className="text-xs font-medium text-gray-700 mb-2 flex items-center">
                        <AlertTriangle className="h-3.5 w-3.5 mr-1 text-orange-500" />
                        Risques Associés ({activity.activity_risks.length})
                      </h4>
                      
                      <div className="space-y-3">
                        {activity.activity_risks.map((risk) => (
                          <div key={risk.id} className="bg-gray-50 rounded-lg p-3">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h5 className="text-sm font-medium text-gray-900">{risk.risk_name}</h5>
                                {risk.description && (
                                  <p className="text-xs text-gray-600 mt-1">{risk.description}</p>
                                )}
                                {risk.consequence && (
                                  <p className="text-xs text-gray-600 mt-1">
                                    <strong>Conséquence:</strong> {risk.consequence}
                                  </p>
                                )}
                              </div>
                              <div className="flex flex-col items-end space-y-1">
                                {getProbabilityBadge(risk.probability)}
                                <span className='px-2 py-0.5 rounded-full text-xs font-medium   bg-green-100 text-green-800'>{risk.status}</span>
                              </div>
                            </div>
                            
                            <div className="text-xs text-gray-500 mb-2">
                              Date du risque: {formatDate(risk.risk_date)}
                            </div>
                            
                            {/* Risk Actions */}
                            {risk.actions && risk.actions.length > 0 && (
                              <div>
                                <h6 className="text-xs font-medium text-gray-700 mb-2">
                                  Actions ({risk.actions.length})
                                </h6>
                                
                                <div className="space-y-2">
                                  {risk.actions.map((action) => (
                                    <div key={action.id} className="bg-white rounded border p-2.5">
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                          <p className="text-xs text-gray-900">{action.action}</p>
                                          {action.dueDate && (
                                            <div className={`flex items-center mt-1 text-xs ${
                                              isOverdue(action.dueDate) ? 'text-red-600' : 'text-gray-500'
                                            }`}>
                                              <Clock className="h-3 w-3 mr-1" />
                                              Échéance: {formatDate(action.dueDate)}
                                              {isOverdue(action.dueDate) && (
                                                <span className="ml-2 px-1 py-0.5 bg-red-100 text-red-800 rounded text-xs">
                                                  En retard
                                                </span>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                        
                                        {action.assigned_user && (
                                          <div className="ml-3 text-right">
                                            <div className="flex items-center text-xs text-gray-900">
                                              <UserCheck className="h-3.5 w-3.5 mr-1 text-green-600" />
                                              {action.assigned_user.first_name} {action.assigned_user.last_name}
                                            </div>
                                            <div className="flex items-center text-xs text-gray-500 mt-0.5">
                                              <Mail className="h-3 w-3 mr-1" />
                                              {action.assigned_user.email}
                                            </div>
                                            {/* {action.assigned_user?.userRoleAssigned && ( */}
                                              <div className="mt-1">
                                                <span className="inline-block px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
                                                  {action.assigned_user.role_function}
                                                </span>
                                              </div>
                                            {/* )} */}

                                            {canAssignTasks(action.has_task) && (
                                              <button 
                                                onClick={() => setConfirmDialog({ show: true, type: 'task', actionId: action.id, activityId: activity.activity_id })}
                                                disabled={isLoading || isCreatingTask === action.id}
                                                className="flex items-center gap-1.5 mt-2 px-2.5 py-1 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed"
                                              >
                                                {isCreatingTask === action.id ? (
                                                  <>
                                                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                    <span>Création...</span>
                                                  </>
                                                ) : (
                                                  <>
                                                    <UserPlus className="w-3.5 h-3.5" />
                                                    <span>Assigner</span>
                                                  </>
                                                )}
                                              </button>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty States */}
        {(!process.associated_activities || process.associated_activities.length === 0) && (
          <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
            <div className="text-center py-6">
              <Activity className="h-10 w-10 text-gray-400 mx-auto mb-3" />
              <h3 className="text-base font-medium text-gray-900 mb-1">Aucune activité associée</h3>
              <p className="text-sm text-gray-500">Ce processus n'a pas encore d'activités associées.</p>
            </div>
          </div>
        )}
        
       
      </div>
    </>
  );
}